import * as React from 'react'
import { useState, useMemo } from 'react'
import type { Resident } from './api'
import { Avatar } from '@/components/primitives/Avatar'
import { Button } from '@/components/primitives/Button'
import { Input } from '@/components/primitives/Input'
import { Spinner } from '@/components/feedback/Spinner'
import './ResidentRail.css'

interface Props {
  residents: Resident[]
  loading: boolean
  selectedId: string | null
  onSelect: (r: Resident) => void
  onAdd: () => void
  hasMore?: boolean
  loadingMore?: boolean
  onLoadMore?: () => void
}

function calcAge(dob: string): number | null {
  const birth = new Date(dob)
  if (isNaN(birth.getTime())) return null
  const today = new Date()
  let age = today.getFullYear() - birth.getFullYear()
  const m = today.getMonth() - birth.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--
  return age
}

function railMeta(r: Resident): string {
  const parts: string[] = []
  if (r.dob) {
    const age = calcAge(r.dob)
    if (age !== null) parts.push(`${age} yrs`)
  }
  if (r.sex) {
    parts.push(r.sex.charAt(0).toUpperCase() + r.sex.slice(1))
  } else if (r.room) {
    parts.push(`Room ${r.room}`)
  }
  return parts.join(' · ') || (r.mrn ?? '—')
}

export function ResidentRail({
  residents,
  loading,
  selectedId,
  onSelect,
  onAdd,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
}: Props): React.JSX.Element {
  const [q, setQ] = useState('')

  const filtered = useMemo(() => {
    let r = [...residents]
    if (q) {
      // Match against the combined "first last" name so a full-name query
      // ("john smith") works — searching the two fields separately never would.
      // The combined string still matches first-only and last-only queries too.
      const needle = q.trim().toLowerCase().replace(/\s+/g, ' ')
      r = r.filter((res) => {
        const fullName = `${res.first_name} ${res.last_name}`.toLowerCase()
        return (
          fullName.includes(needle) ||
          (res.mrn?.toLowerCase().includes(needle) ?? false) ||
          (res.room?.toLowerCase().includes(needle) ?? false)
        )
      })
    }
    return r
  }, [q, residents])

  return (
    <aside className="rail" aria-label="Residents">
      <div className="rail-header">
        <h2 className="rail-title">Residents</h2>
        <Button variant="primary" size="sm" onClick={onAdd} leftIcon={<PlusIcon />}>
          Add
        </Button>
      </div>
      <div className="rail-search">
        <SearchIcon />
        <Input
          type="search"
          placeholder="Search residents..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Search residents"
        />
      </div>
      <div className="rail-body">
        {loading ? (
          <div className="rail-loading">
            <Spinner />
          </div>
        ) : filtered.length === 0 ? (
          <div className="rail-empty">
            {q ? 'No matches' : 'No residents yet'}
          </div>
        ) : (
          <ul className="rail-list" role="listbox" aria-label="Residents list">
            {filtered.map((r) => {
              const active = r.id === selectedId
              return (
                <li key={r.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={active}
                    className={'rail-item' + (active ? ' rail-item-active' : '')}
                    onClick={() => onSelect(r)}
                  >
                    <Avatar
                      name={`${r.first_name} ${r.last_name}`}
                      size="md"
                      active={active}
                    />
                    <span className="rail-item-text">
                      <span className="rail-item-name">
                        {r.first_name} {r.last_name}
                      </span>
                      <span className="rail-item-meta">{railMeta(r)}</span>
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        )}
        {!loading && hasMore && !q && (
          <div className="rail-load-more">
            <button
              type="button"
              className="rail-load-more-btn"
              onClick={onLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          </div>
        )}
      </div>
    </aside>
  )
}

function SearchIcon(): React.JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="rail-search-icon">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  )
}

function PlusIcon(): React.JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  )
}
